import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react'
import styles from './MicrositeSocial.module.css'
import { BASE_URL_API } from '../../../References/URLs'
import AuthContext from '../../../store/auth-context'
import ActiveStoreContext from '../../../store/ActiveStoreContext'
import { Check, AlertCircle, RefreshCw, Plus } from 'lucide-react'

import MicrositePageHeader from '../components/MicrositePageHeader/MicrositePageHeader'
import MicrositeSortableSocialList from '../components/MicrositeSortableSocialList/MicrositeSortableSocialList'
import SelectInput from '../components/SelectInput/SelectInput'
import MicrositeVersionDrawer from '../components/MicrositeVersionDrawer/MicrositeVersionDrawer'

const PLATFORM_OPTIONS = [
    { value: 'instagram', label: 'Instagram' },
    { value: 'facebook', label: 'Facebook' },
    { value: 'twitter', label: 'Twitter / X' },
    { value: 'youtube', label: 'YouTube' },
    { value: 'linkedin', label: 'LinkedIn' },
    { value: 'whatsapp', label: 'WhatsApp' }
]

const MicrositeSocial = () => {
    const authCtx = useContext(AuthContext)
    const asCtx   = useContext(ActiveStoreContext)
    const activeStore = useMemo(() => {
        try {
            return (asCtx?.activeStore && asCtx.activeStore.trim() !== '')
                ? JSON.parse(asCtx.activeStore)
                : null
        } catch {
            return null
        }
    }, [asCtx?.activeStore])

    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [historyOpen, setHistoryOpen] = useState(false)

    const [links, setLinks] = useState([])
    const [original, setOriginal] = useState([])
    const [newPlatform, setNewPlatform] = useState('instagram')

    const isDirty = useMemo(() => JSON.stringify(links) !== JSON.stringify(original), [links, original])

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3000)
    }

    const post = useCallback(async (resource, payload = {}) => {
        try {
            const res = await fetch(BASE_URL_API + resource, {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx?.token,
                    storeIdentifier: activeStore?.storeIdentifier,
                    vendorIdentifier: JSON.parse(authCtx?.loginData)?.partnersData?.vendorIdentifier,
                    ...payload
                })
            })
            if (!res.ok) return null
            const data = await res.json()
            if (data?.message === 'tokenExpired') {
                authCtx.logout()
                return null
            }
            return data
        } catch {
            return null
        }
    }, [authCtx, activeStore])

    const loadSocial = useCallback(async () => {
        setLoading(true)
        const res = await post('micrositeGetStoreConfig')
        if (res?.status === 'success') {
            const social = (res.response?.social || []).map((l, i) => ({ ...l, sortOrder: i }))
            setLinks(social)
            setOriginal(social)
        } else {
            showToast('Could not load social links. Please refresh.', 'error')
        }
        setLoading(false)
    }, [post])

    useEffect(() => {
        if (activeStore?.storeIdentifier) loadSocial()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeStore?.storeIdentifier])

    const handleAddLink = () => {
        if (links.some((l) => l.platform === newPlatform)) {
            showToast('That platform is already added', 'error')
            return
        }
        setLinks((prev) => [...prev, { platform: newPlatform, url: '', isVisible: true, sortOrder: prev.length }])
    }

    const handleSave = async () => {
        setSaving(true)
        const res = await post('micrositeUpdateStoreSocial', { links })
        setSaving(false)

        if (res?.status === 'success') {
            setOriginal(links)
            showToast('Social links saved successfully')
        } else {
            showToast('Could not save social links', 'error')
        }
    }

    const availablePlatforms = PLATFORM_OPTIONS.filter(
        (opt) => !links.some((l) => l.platform === opt.value)
    )

    if (loading) {
        return (
            <div className={styles.loadingWrap}>
                <RefreshCw size={18} className={styles.spinner} />
                <span>Loading social links…</span>
            </div>
        )
    }

    return (
        <div className={styles.page}>
            {toast && (
                <div className={`${styles.toast} ${styles[`toast_${toast.type}`]}`}>
                    {toast.type === 'success' ? <Check size={14} /> : <AlertCircle size={14} />}
                    {toast.message}
                </div>
            )}

            <MicrositePageHeader
                title="Social links"
                subtitle="Connect your salon's social profiles — shown in your footer and contact section."
                isDirty={isDirty}
                saving={saving}
                onSave={handleSave}
                onHistory={() => setHistoryOpen(true)}
            />

            <section className={styles.fieldGroup}>
                <h2 className={styles.groupTitle}>Your social profiles</h2>
                <p className={styles.groupDesc}>Drag to reorder how they appear on your site.</p>

                {links.length === 0 ? (
                    <div className={styles.emptyState}>No social links added yet.</div>
                ) : (
                    <MicrositeSortableSocialList links={links} onChange={setLinks} />
                )}

                {availablePlatforms.length > 0 && (
                    <div className={styles.addRow}>
                        <div className={styles.addSelectWrap}>
                            <SelectInput
                                value={newPlatform}
                                onChange={setNewPlatform}
                                options={availablePlatforms}
                            />
                        </div>
                        <button type="button" className={styles.addBtn} onClick={handleAddLink}>
                            <Plus size={14} />
                            Add platform
                        </button>
                    </div>
                )}
            </section>

            <MicrositeVersionDrawer
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                tabKey="social"
                storeIdentifier={activeStore?.storeIdentifier}
                vendorIdentifier={activeStore?.vendorIdentifier}
                onRolledBack={loadSocial}
            />
        </div>
    )
}

export default MicrositeSocial